import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Globe, Plus, Sparkles, Trash2, ExternalLink, Loader2, Rocket } from "lucide-react";
import { toast } from "sonner";

export default function Websites() {
  const nav = useNavigate();
  const { refreshUser } = useAuth();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/websites").then((r) => setItems(r.data.items)).catch(() => {});
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!prompt.trim()) return toast.error("Describe your website");
    setBusy(true);
    try {
      const { data } = await api.post("/websites", { prompt });
      toast.success("Website generated");
      refreshUser();
      nav(`/websites/${data.website_id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this website?")) return;
    await api.delete(`/websites/${id}`);
    load();
  };

  return (
    <div className="p-8 max-w-[1600px] mx-auto">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="kr-overline text-[#00E5FF]">[ WEBSITE BUILDER ]</div>
          <h1 className="font-display text-4xl mt-2 tracking-tight" data-testid="websites-title">Websites</h1>
          <p className="text-white/55 text-sm mt-1.5">Describe your idea. AlphaCore builds a publishable website in seconds.</p>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <Button onClick={() => setOpen(true)} className="bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90 rounded-full" data-testid="new-website-btn"><Plus className="h-4 w-4 mr-2" /> Create website</Button>
          <DialogContent className="bg-[#0A0A0E] border-white/10 text-white max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-display">Describe your website</DialogTitle>
              <DialogDescription className="text-white/50">AlphaCore will design the pages, colors, sections, and content.</DialogDescription>
            </DialogHeader>
            <Textarea data-testid="website-prompt-input" rows={5} value={prompt} onChange={(e) => setPrompt(e.target.value)} placeholder="A boutique specialty coffee brand in Berlin with subscription, retail locations, and a story-led about page…" className="bg-[#050505] border-white/10 resize-none" />
            <div className="text-xs text-white/40 font-mono">15 credits · generates hero + features + testimonials + pricing + CTA + footer</div>
            <DialogFooter>
              <Button onClick={create} disabled={busy} className="bg-[#00E5FF] text-black hover:bg-[#00E5FF]/90 w-full" data-testid="create-website-confirm">
                {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Sparkles className="h-4 w-4 mr-2" /> Generate website</>}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((w, i) => (
          <motion.div key={w.website_id} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.04 }} className="kr-card p-6 relative group" data-testid={`website-card-${w.website_id}`}>
            <Link to={`/websites/${w.website_id}`} className="absolute inset-0 z-0" />
            <div className="relative z-10 pointer-events-none">
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 border border-white/10 flex items-center justify-center" style={{ color: w.theme?.primary || "#00E5FF" }}>
                  <Globe className="h-5 w-5" strokeWidth={1.5} />
                </div>
                <button onClick={(e) => { e.preventDefault(); e.stopPropagation(); remove(w.website_id); }} className="opacity-0 group-hover:opacity-100 text-white/40 hover:text-red-300 transition pointer-events-auto z-20" data-testid={`delete-website-${w.website_id}`}>
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <div className="font-display text-lg mt-5">{w.name}</div>
              <div className="text-sm text-white/50 mt-1 line-clamp-2 min-h-[2.5rem]">{w.description}</div>
              <div className="mt-5 flex items-center gap-2 text-xs">
                {w.published ? (
                  <span className="inline-flex items-center gap-1.5 text-[#34D399]"><span className="w-1.5 h-1.5 rounded-full bg-[#34D399] kr-pulse-soft" /> published</span>
                ) : (
                  <span className="text-white/40">draft</span>
                )}
                <span className="text-white/30 font-mono ml-auto">/{w.slug}</span>
              </div>
            </div>
          </motion.div>
        ))}
        {items.length === 0 && (
          <div className="col-span-full text-center py-20 text-white/40">
            <Rocket className="h-10 w-10 mx-auto mb-3" />
            <div className="text-sm">No websites yet. Describe your idea to create the first.</div>
          </div>
        )}
      </div>
    </div>
  );
}
